import Image from 'next/image'
import NextLink from 'next/link'
import { type FC } from 'react'

import { type CrossSellProduct } from '@redteclab/api/clients/retail-media-xsell-service'

import { Skeleton } from 'base-ui'

import { useExperimentalAddStaticFileHost } from '../../../experiments/new-cdn-experiment/useExperimentalAddStaticFileHost'
import { useGlobalConfigContext } from '../../../global-config'
import {
  IMAGE_PRODUCT_FALLBACK_SRC,
  useCdnHostedImageLoader,
} from '../../../image'
import { urlGetRelativeUrl } from '../../../url-handling'
import { crossSellAccessorGetLocalizedProductDeepLink } from '../../model/crossSellAccessorGetLocalizedProductDeepLink'

type CrossSellHorizontalSliderItemCompactImageProps = {
  product?: CrossSellProduct
}

const MAX_IMAGE_SIZE = 160

export const CrossSellHorizontalSliderItemCompactImage: FC<
  CrossSellHorizontalSliderItemCompactImageProps
> = ({ product }) => {
  const loader = useCdnHostedImageLoader()
  const { addStaticCdnHostToUrl } = useExperimentalAddStaticFileHost()
  const { language, tenant } = useGlobalConfigContext()

  if (!product) {
    return (
      <Skeleton className="px-7 py-3.5 tablet:p-3.5" variant="rectangular">
        <div className="aspect-square w-full" />
      </Skeleton>
    )
  }

  return (
    <NextLink
      className="relative px-7 py-3.5 after:absolute after:inset-0 after:bg-gradient-radial after:from-transparent after:via-transparent after:to-[var(--background-light-primary-medium)] tablet:p-3.5"
      data-qa-id={`form-product-slider-${product.id}-image-button`}
      href={urlGetRelativeUrl(
        crossSellAccessorGetLocalizedProductDeepLink({
          language,
          product,
          tenant,
        }),
      )}
    >
      <div className="flex aspect-square max-h-[130px] w-full items-center justify-center p-3">
        <Image
          alt={product.name}
          className="max-h-full w-auto max-w-full"
          data-analytics="item-image"
          height={MAX_IMAGE_SIZE}
          loader={loader}
          src={
            product.iconurl || addStaticCdnHostToUrl(IMAGE_PRODUCT_FALLBACK_SRC)
          }
          width={MAX_IMAGE_SIZE}
        />
      </div>
    </NextLink>
  )
}
